import { BookingLanguage } from "@/types/booking";
import { ConsentDetailsLink } from "@ahead-health/core/ui/cookieConsent";
import { Lock } from "lucide-react";
import React from "react";
import { useTranslation } from "react-i18next";
import GoogleStarRating from "../GoogleStarRating";
import { languageMap } from "@/i18n";
import { useMarket } from "@/hooks/useMarket";
import { getLegalUrls } from "@/config/market";

interface FooterProps {
  language: BookingLanguage;
}

const customGray = "#F6F6F6"; // neutral-50

const Footer = ({ language }: FooterProps) => {
  const { t } = useTranslation(undefined, { lng: languageMap[language] });
  const { showsTwint, market } = useMarket();
  const { pp: privacyPolicyUrl, tos: termsConditionsUrl } = getLegalUrls(
    market,
    language,
  );

  return (
    <footer className="bg-gray-50 pb-8 px-2">
      {/* Security Row */}
      <div
        className="flex items-center justify-center mx-auto max-w-[700px] mb-3 h-[54px]"
        style={{ marginBottom: 18, height: 54 }}
      >
        <div
          className="flex items-center gap-1 text-base text-gray-800 h-full px-7"
          style={{ background: customGray, borderRadius: 20 }}
        >
          <Lock className="h-6 w-6" />
          <span>
            {t("footer.dataSecure")}{" "}
            <a
              href={privacyPolicyUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="underline hover:text-gray-600 transition-colors"
            >
              {t("footer.securelyStored")}
            </a>
          </span>
        </div>
      </div>

      {/* Reviews Row */}
      <div className="flex justify-center w-full mb-6">
        <a
          href="https://www.google.com/maps/place/Ahead+Health/@47.3734654,8.5333993,17z/data=!3m1!4b1!4m6!3m5!1s0x47900b29f69aa4c5:0xec34f49e3ca9ce2e!8m2!3d47.3734618!4d8.5359742!16s%2Fg%2F11x1bl_tpp?entry=ttu&g_ep=EgoyMDI1MTExNy4wIKXMDSoASAFQAw%3D%3D"
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center"
          style={{
            background: customGray,
            borderRadius: 20,
            padding: "12px 20px",
          }}
        >
          <GoogleStarRating className="w-auto h-[30px]" />
        </a>
      </div>

      {/* Payment logos */}
      <div className="flex flex-row flex-wrap justify-center items-center gap-2 mt-8">
        <div className="w-10 h-6 rounded flex items-center justify-center bg-white border-gray-200 border overflow-hidden">
          <img
            src="/klarna.svg"
            alt="Klarna"
            className="w-full object-contain"
          />
        </div>
        {showsTwint && (
          <div className="w-10 h-6 rounded flex items-center justify-center bg-white border-gray-200 border overflow-hidden">
            <img
              src="/twint.svg"
              alt="Twint"
              className="w-full object-contain"
            />
          </div>
        )}
        <div className="w-10 h-6 rounded flex items-center justify-center bg-white border-gray-200 border overflow-hidden">
          <img
            src="/paypal.svg"
            alt="PayPal"
            className="h-full w-full object-contain"
          />
        </div>
        <div className="w-10 h-6 rounded flex items-center justify-center bg-white border-gray-200 border overflow-hidden">
          <img
            src="/visa.svg"
            alt="Visa"
            className="h-full w-full object-contain"
          />
        </div>
        <div className="w-10 h-6 rounded flex items-center justify-center bg-white border-gray-200 border overflow-hidden">
          <img
            src="/apple-pay.svg"
            alt="Apple Pay"
            className="h-full w-full object-contain"
          />
        </div>
        <div className="w-10 h-6 rounded flex items-center justify-center bg-white border-gray-200 border overflow-hidden">
          <img
            src="/google-pay.svg"
            alt="Google Pay"
            className="h-full w-full object-contain"
          />
        </div>
        <div className="w-10 h-6 rounded flex items-center justify-center bg-white border-gray-200 border overflow-hidden">
          <img
            src="/mastercard.svg"
            alt="Mastercard"
            className="h-full w-full object-contain"
          />
        </div>
        <div className="w-10 h-6 rounded flex items-center justify-center bg-white border-gray-200 border overflow-hidden">
          <img
            src="/amex.svg"
            alt="American Express"
            className="w-full object-contain"
          />
        </div>
      </div>

      {/* Copyright and Legal Links Row */}
      <div className="flex items-center justify-center mx-auto w-full mt-12 pb-12">
        <div className="flex lg:flex-row flex-col items-center justify-center lg:gap-8 gap-2 text-sm text-gray-600">
          <span>
            ©{new Date().getFullYear()}{" "}
            <a
              href={t("footer.homeUrl")}
              target="_blank"
              rel="noopener noreferrer"
              className="hover:text-gray-800 transition-colors"
            >
              Ahead Health AG
            </a>
          </span>
          <a
            href={privacyPolicyUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="hover:text-gray-800 transition-colors"
          >
            {t("footer.privacyPolicy")}
          </a>
          <a
            href={termsConditionsUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="hover:text-gray-800 transition-colors"
          >
            {t("footer.termsConditions")}
          </a>
          {/* Manage cookie consent. ConsentDetailsLink renders nothing for
              Swiss visitors and before the consent system mounts (see
              @ahead-health/core), so it cleanly drops out as a flex item. */}
          <ConsentDetailsLink className="hover:text-gray-800 transition-colors">
            {t("footer.cookieSettings")}
          </ConsentDetailsLink>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
